import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Search, Users, Link2 } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Loader from "../common/Loader";
import EmptyState from "../common/EmptyState";
import { useAuth } from "../../context/AuthContext";
import { resolveCurrentUserId } from "../../utils/eventPermissions";
import { extractListResult } from "../../utils/apiHelpers";
import {
  generateBusinessAssistantInvite,
  searchConfirmedUsers,
  createBusinessAssistant,
  getBusinessAssistants,
} from "../../services/eventDetailService";
import { color } from "../../styles/theme";

/**
 * Invite Business Assistant, ported from mobile's invite-ba/index.js
 * (share QR/link) + invite-ba-list/index.js (search your confirmed users
 * and invite one directly) — combined into two tabs of one modal instead of
 * mobile's two separate screens, matching how this panel already
 * consolidates multi-screen mobile flows.
 */
const InviteBusinessAssistantModal = ({ eventId, onClose }) => {
  const { t } = useTranslation("eventDetails");
  const { user } = useAuth();
  const currentUserId = resolveCurrentUserId(user);
  const [tab, setTab] = useState("search"); // 'search' | 'share'

  return (
    <Modal title={t("inviteBusinessAssistantModal.title")} onClose={onClose} maxWidth={440}>
      <div style={styles.tabRow}>
        <button type="button" className={`tab-btn${tab === "search" ? " active" : ""}`} onClick={() => setTab("search")} style={styles.tabBtn}>
          <Search size={13} strokeWidth={2.25} style={styles.tabIcon} /> {t("inviteBusinessAssistantModal.tabs.search")}
          <span className="tab-indicator" />
        </button>
        <button type="button" className={`tab-btn${tab === "share" ? " active" : ""}`} onClick={() => setTab("share")} style={styles.tabBtn}>
          <Link2 size={13} strokeWidth={2.25} style={styles.tabIcon} /> {t("inviteBusinessAssistantModal.tabs.share")}
          <span className="tab-indicator" />
        </button>
      </div>

      {tab === "search" ? (
        <SearchInviteTab eventId={eventId} currentUserId={currentUserId} />
      ) : (
        <ShareLinkTab eventId={eventId} />
      )}
    </Modal>
  );
};

const PAGE_SIZE = 20;

const SearchInviteTab = ({ eventId, currentUserId }) => {
  const { t } = useTranslation("eventDetails");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [invitingId, setInvitingId] = useState(null);
  const [invitedIds, setInvitedIds] = useState(new Set());
  const requestId = useRef(0);
  const debounceRef = useRef(null);

  const loadExclusions = () =>
    getBusinessAssistants(eventId, { pageNumber: 1, pageSize: 100 })
      .then((res) => {
        const list = extractListResult(res.data);
        const ids = list.map((a) => a?.AssistantUserId ?? a?.AppUser?.Id ?? a?.UserId).filter((v) => v != null);
        setExcludedIds(new Set(ids.map(String)));
      })
      .catch(() => {});

  const fetchUsers = (pageNumber, searchText) => {
    const thisRequest = ++requestId.current;
    (pageNumber === 1 ? setLoading : setLoadingMore)(true);
    searchConfirmedUsers({ pageNumber, pageSize: PAGE_SIZE, search: searchText })
      .then((res) => {
        if (thisRequest !== requestId.current) return; // stale response, discard
        const newData = extractListResult(res.data);
        setUsers((prev) => (pageNumber === 1 ? newData : [...prev, ...newData]));
        setMeta(res.data?.Metadata || null);
        setPage(pageNumber);
      })
      .catch(() => {
        if (thisRequest === requestId.current) toast.error(t("inviteBusinessAssistantModal.searchTab.loadUsersError"));
      })
      .finally(() => {
        if (thisRequest === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      });
  };

  useEffect(() => {
    loadExclusions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(1, search), 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const visibleUsers = useMemo(
    () => users.filter((u) => {
      const id = String(u?.UserId ?? u?.Id ?? "");
      return id && id !== String(currentUserId) && !excludedIds.has(id);
    }),
    [users, excludedIds, currentUserId]
  );

  const hasMore = meta && users.length < (meta.TotalCount ?? meta.totalCount ?? 0);

  const handleInvite = async (targetUser) => {
    const userId = targetUser?.UserId ?? targetUser?.Id;
    if (!userId) return;
    setInvitingId(userId);
    try {
      const res = await createBusinessAssistant(eventId, userId);
      toast.success(res.data?.Message || t("inviteBusinessAssistantModal.searchTab.invitedToast", { name: targetUser?.NickName || t("inviteBusinessAssistantModal.searchTab.defaultUserName") }));
      setInvitedIds((prev) => new Set(prev).add(String(userId)));
    } catch (err) {
      toast.error(err.response?.data?.Message || t("inviteBusinessAssistantModal.searchTab.inviteError"));
    } finally {
      setInvitingId(null);
    }
  };

  return (
    <div>
      <div style={styles.searchWrap}>
        <Search size={15} strokeWidth={2} color={color.gray400} style={styles.searchIcon} />
        <input
          type="text"
          placeholder={t("inviteBusinessAssistantModal.searchTab.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input"
          style={styles.searchInput}
        />
      </div>

      {loading ? (
        <Loader />
      ) : visibleUsers.length === 0 ? (
        <EmptyState icon={Users} text={search ? t("inviteBusinessAssistantModal.searchTab.noMatches") : t("inviteBusinessAssistantModal.searchTab.noneAvailable")} />
      ) : (
        <div style={styles.userList}>
          {visibleUsers.map((u) => {
            const userId = u?.UserId ?? u?.Id;
            const alreadyInvited = invitedIds.has(String(userId));
            return (
              <div key={userId} style={styles.userRow}>
                {u?.DisplayPicture ? (
                  <img src={u.DisplayPicture} alt="" style={styles.avatar} />
                ) : (
                  <div style={styles.avatarFallback}>{(u?.NickName || "?").charAt(0).toUpperCase()}</div>
                )}
                <span style={styles.userName}>{u?.NickName || t("inviteBusinessAssistantModal.searchTab.unnamed")}</span>
                <button
                  type="button"
                  className={`btn btn-sm ${alreadyInvited ? "btn-secondary" : "btn-primary"}`}
                  onClick={() => handleInvite(u)}
                  disabled={invitingId === userId || alreadyInvited}
                >
                  {alreadyInvited ? t("inviteBusinessAssistantModal.searchTab.invited") : invitingId === userId ? t("inviteBusinessAssistantModal.searchTab.inviting") : t("inviteBusinessAssistantModal.searchTab.invite")}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && !loading && (
        <div style={styles.loadMoreRow}>
          <button type="button" className="btn btn-secondary" onClick={() => fetchUsers(page + 1, search)} disabled={loadingMore}>
            {loadingMore ? t("inviteBusinessAssistantModal.searchTab.loading") : t("inviteBusinessAssistantModal.searchTab.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
};

const ShareLinkTab = ({ eventId }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    generateBusinessAssistantInvite(eventId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.Result ?? res.data;
        // This endpoint's response shape differs from the other QR endpoints
        // (EventQR/SubPromoterQR/IndividualQR use Bytes+ChatRoomLink) — verified
        // against the real API: it returns QRBytes+DynamicLink instead. The
        // QR image itself is intentionally not shown here — link only.
        const link = data?.DynamicLink ?? data?.ChatRoomLink ?? data?.Link ?? null;
        if (res.data?.Status !== "Success" || !link) throw new Error(res.data?.Message || "Failed");
        setResult({ link });
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [eventId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.link);
      toast.success(t("inviteBusinessAssistantModal.shareTab.linkCopied"));
    } catch {
      toast.error(t("inviteBusinessAssistantModal.shareTab.copyError"));
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: t("inviteBusinessAssistantModal.shareTab.shareTitle"), url: result.link });
        return;
      } catch {
        // fall through to copy
      }
    }
    copyLink();
  };

  return (
    <div>
      {loading && <Loader />}
      {!loading && error && <EmptyState text={t("inviteBusinessAssistantModal.shareTab.generateError")} />}
      {!loading && result && (
        <div style={styles.resultWrap}>
          <input readOnly value={result.link} className="input" style={styles.linkInput} onFocus={(e) => e.target.select()} />
          <div style={styles.actions}>
            <Button variant="secondary" onClick={copyLink}>{t("inviteBusinessAssistantModal.shareTab.copyLink")}</Button>
            <Button onClick={shareLink}>{t("common:actions.share")}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  tabRow: { display: "flex", gap: 22, marginBottom: 18, borderBottom: `1px solid ${color.border}` },
  tabBtn: { display: "inline-flex", alignItems: "center" },
  tabIcon: { marginRight: 6 },
  searchWrap: { position: "relative", marginBottom: 14 },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" },
  searchInput: { paddingLeft: 34 },
  userList: { display: "flex", flexDirection: "column", gap: 4, maxHeight: 320, overflowY: "auto" },
  userRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 4px" },
  avatar: { width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: color.gray100 },
  avatarFallback: {
    width: 30, height: 30, borderRadius: "50%", background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, flexShrink: 0,
  },
  userName: { fontSize: 13, color: color.textPrimary, fontWeight: 500, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  loadMoreRow: { textAlign: "center", marginTop: 10 },
  resultWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 14 },
  linkInput: { width: "100%", fontSize: 12, color: color.textSecondary, background: color.gray50 },
  actions: { display: "flex", gap: 10 },
};

export default InviteBusinessAssistantModal;
